import { useState, type ReactNode } from "react";
import { ProductShot } from "./ProductShot";
import shotDashboard from "../../assets/landing/app-dashboard.png";
import shotPolicies from "../../assets/landing/policies.png";
import shotWorkspace from "../../assets/landing/calc-workspace.png";

type Tab = {
  key: string;
  label: string;
  headline: string;
  mechanic: string;
  points: string[];
  /** Mono spec rows — concrete facts that fill the copy column (R5 density). */
  facts: [string, string][];
  shot: ReactNode;
};

/* Three tabs show REAL captures of the running product (ProductShot). The two
 * below are styled artifacts for surfaces still being built — swap for real
 * captures when they exist (tracked in CONTENT_MAP.md). */
function HarnessShot() {
  return (
    <div className="shot">
      <div className="shot-head"><span>AI guardrails</span><span className="sample-pill">Sample</span></div>
      <div className="shot-row"><span className="shot-dot" /><span className="shot-name">Max utilization</span><span className="shot-tag">DCR ≤ 0.90</span></div>
      <div className="shot-row"><span className="shot-dot" /><span className="shot-name">Live-load defl.</span><span className="shot-tag">≤ L/360</span></div>
      <div className="shot-row"><span className="shot-dot" /><span className="shot-name">Ask before</span><span className="shot-tag">changing material</span></div>
      <div className="shot-ai-q">Learned “prefer 16-inch on-center joists” from your edits — retune anytime.</div>
    </div>
  );
}
function OrgShot() {
  return (
    <div className="shot">
      <div className="shot-head"><span>Organization</span><span className="sample-pill">Sample</span></div>
      <div className="shot-row"><span className="shot-av">JH</span><span className="shot-name">Jordan H. — PE of record</span><span className="shot-tag">Stamp</span></div>
      <div className="shot-row"><span className="shot-av">MA</span><span className="shot-name">Maya A. — Engineer</span><span className="shot-tag">Design</span></div>
      <div className="shot-row"><span className="shot-av">RB</span><span className="shot-name">Rosa B. — Builder</span><span className="shot-tag">View · request</span></div>
      <div className="shot-foot"><span className="shot-mono">3 orgs · roles &amp; audit per firm</span></div>
    </div>
  );
}

const TABS: Tab[] = [
  {
    key: "sites",
    label: "Projects & plans",
    headline: "Every lot on one board, intake to stamped set.",
    mechanic: "Each project tracks its inputs, design pipeline, and review state; reuse a master plan across lots or import a custom one.",
    points: ["Pipeline progress per lot, at a glance", "Curated site, hazard, soil & plan inputs", "Reuse a master plan across every lot"],
    facts: [
      ["Pipeline", "6 stages · plan → stamped set"],
      ["Inputs", "site · hazard · soil · foundation · profile · plan"],
      ["Statuses", "draft · active · in review · approved"],
    ],
    shot: (
      <ProductShot
        src={shotDashboard}
        alt="The Firmark dashboard: nine projects with per-lot pipeline progress, input completeness, and review statuses"
      />
    ),
  },
  {
    key: "design",
    label: "Design policies",
    headline: "Set your firm's standards once, apply them everywhere.",
    mechanic: "Policy profiles capture code editions, loads, materials, and defaults as reusable presets. Each project can override, and overrides are logged.",
    points: ["ASD / LRFD profiles, cloned from vetted presets", "Code editions, wind, snow, seismic, in one place", "Applies to every project that uses it"],
    facts: [
      ["Presets", "4 built-in · read-only starting points"],
      ["Scope", "code editions · wind · snow · seismic · members"],
      ["Overrides", "per-project · every change logged"],
    ],
    shot: (
      <ProductShot
        src={shotPolicies}
        alt="The Policies page: built-in design policy profiles including Residential Engineered Wood ASD (active) and LRFD variants"
      />
    ),
  },
  {
    key: "engineering",
    label: "Member checks",
    headline: "Every member sized and checked to the governing clause.",
    mechanic: "A deterministic, code-traceable calc engine: same plan, same result, every run. The sheet shown is real, and every check on it cites its NDS clause.",
    points: ["Every limit state checked, with its clause", "Autosize scores the whole catalog live", "Same plan → the same result"],
    facts: [
      ["Checks", "moment · shear · bearing · deflection · stability"],
      ["Cited", "NDS-2024 3.3 · 3.4 · 3.10 · IBC 1604.3"],
      ["Autosize", "167 sections scored per member"],
    ],
    shot: (
      <ProductShot
        src={shotWorkspace}
        alt="A floor-joist calc sheet: moment, shear, bearing and deflection checks passing with NDS-2024 clause references, drawn-to-scale loading diagram, and a live member selector"
      />
    ),
  },
  {
    key: "harness",
    label: "AI guardrails",
    headline: "Steer AI: set the guardrails, see what it learned.",
    mechanic: "You set the standards it works within; it shows what it picked up and lets you retune.",
    points: ["Set max utilization & deflection caps", "Ask-before rules on material changes", "See what it learned; retune anytime"],
    facts: [
      ["Caps", "DCR ≤ 0.90 · LL defl ≤ L/360"],
      ["Asks first", "material swaps · envelope steps"],
      ["Learned", "from your edits · retune anytime"],
    ],
    shot: <HarnessShot />,
  },
  {
    key: "org",
    label: "Org management",
    headline: "Roles, firms, and an audit trail that holds up.",
    mechanic: "One account spans many firms; each PE, engineer, and builder gets the right access.",
    points: ["PE, engineer, builder roles", "One account, many firms, fully isolated", "Full audit trail per firm"],
    facts: [
      ["Roles", "PE · engineer · builder · least privilege"],
      ["Isolation", "tenant-fenced per firm · enforced"],
      ["Audit", "append-only · who · what · when"],
    ],
    shot: <OrgShot />,
  },
];

export function ToolShowcase() {
  const [tab, setTab] = useState(TABS[0].key);
  const active = TABS.find((t) => t.key === tab) ?? TABS[0];

  return (
    <section className="landing-band" id="platform">
      <div className="landing-wrap landing-wrap--wide landing-section">
        <div className="section-head">
          <h2 className="section-title">One harness, every facet of the job.</h2>
          <p className="section-sub">
            Your sites and plans, firm-wide design standards, a code-traceable calc engine, AI
            guardrails, and org admin. One system that holds the whole workflow.
          </p>
        </div>

        <div className="showcase-rail" role="group" aria-label="Choose a part of the harness">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-pressed={tab === t.key}
              className={`showcase-tab${tab === t.key ? " is-on" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="showcase-panel">
          <div className="showcase-copy" aria-live="polite">
            <h3 className="showcase-headline">{active.headline}</h3>
            <p className="showcase-mechanic">{active.mechanic}</p>
            <ul className="fork-list showcase-points">
              {active.points.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <dl className="showcase-facts">
              {active.facts.map(([k, v]) => (
                <div className="showcase-fact" key={k}>
                  <dt>{k}</dt>
                  <dd className="shot-mono">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="showcase-shot">{active.shot}</div>
        </div>
      </div>
    </section>
  );
}
